import { useState } from "react";
import NavigationBar from "../../navigation/nav-bar/navigationBar";
import FooterComponent from "../../navigation/footer/footerComponent";

// ── Shared styles ─────────────────────────────────────────────────────────────

const fadeIn = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=DM+Sans:wght@300;400;500;600&display=swap');

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}
.anim-1 { animation: fadeUp 0.7s ease both; }
.anim-2 { animation: fadeUp 0.7s 0.12s ease both; }
.anim-3 { animation: fadeUp 0.7s 0.24s ease both; }
.anim-4 { animation: fadeUp 0.7s 0.36s ease both; }
.anim-fade { animation: fadeIn 1.2s ease both; }
.display-font { font-family: 'Playfair Display', Georgia, serif; }
.body-font { font-family: 'DM Sans', sans-serif; }
.grain {
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
  background-repeat: repeat;
  background-size: 128px;
  pointer-events: none;
}
`;

// ── FAQ component ─────────────────────────────────────────────────────────────

const faqs = [
    {
        q: "Do I need coding or business experience?",
        a: "No. Most students who join have never built a product before. You'll be placed in a role that fits your strengths: Product, Engineering, Design, Marketing, Growth, or Operations. You learn by doing, not by watching.",
    },
    {
        q: "How much time does this take each week?",
        a: "The 2027 Startup Sprint is 5–10 hours per week over 14 days. The Founder Track is 8–12 hours per week over 28 days. The Residency runs about 3–4 hours per day over 48 days. All programs are designed to fit around school, but the deadlines are real.",
    },
    {
        q: "What does a typical week look like?",
        a: "Two or three structured standups with your Startup Trainer and team. Async work in between, building, designing, writing, testing, or shipping, depending on your role. Each week ends with something done.",
    },
    {
        q: "What role will I be in?",
        a: "You'll apply with your interests and background in mind. The six roles are Product Lead, Engineering, Design, Marketing, Growth, and Operations. You own your domain for the full program — you aren't rotating, you're going deep.",
    },
    {
        q: "What is the Startup Trainer?",
        a: "Every team is guided by a vetted founder who has raised over $1 million in funding and scaled a real product to 100,000+ users. They run your standups, stay on Slack throughout the program, and write your letter of recommendation.",
    },
    {
        q: "What do I walk away with?",
        a: "A live consumer product that stays online for a full year. A certificate of completion. A letter of recommendation from your startup trainer. And a story you'll be able to tell in every college interview, application essay, and job for years.",
    },
    {
        q: "What happens on Demo Day?",
        a: "Your team presents the product you built. You demo what you actually shipped. It's the initial finish line — and usually the moment it clicks that you actually did this.",
    },
    {
        q: "Can I do this while I'm in school?",
        a: "Yes. The program is structured to run alongside your regular schedule. Meeting times are coordinated around your team, and the async work fits in wherever you have time. A lot of our students are in school while they build.",
    },
];

function FAQ({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b" style={{ borderColor: "rgba(168,150,128,0.25)" }}>
            <button
                className="w-full text-left py-5 flex justify-between items-start gap-4"
                onClick={() => setOpen(!open)}
            >
                <span className="body-font text-stone-800 text-base leading-snug" style={{ fontWeight: 500 }}>{q}</span>
                <span
                    className="text-2xl leading-none mt-0.5 flex-shrink-0 transition-transform duration-300"
                    style={{ color: "#b07d4a", transform: open ? "rotate(45deg)" : "rotate(0deg)" }}
                >+</span>
            </button>
            {open && (
                <p className="body-font text-stone-500 pb-5 leading-relaxed text-sm md:text-base" style={{ fontWeight: 300 }}>{a}</p>
            )}
        </div>
    );
}

// ── CTA section ───────────────────────────────────────────────────────────────

const ctaCards = [
    {
        label: "Start here",
        title: "Apply Free",
        desc: "The application takes 30 minutes and costs nothing. Cohorts are small. Spots close when they fill.",
        href: "/apply",
        primary: true,
    },
    {
        label: "14 days",
        title: "2027 Startup Sprint",
        desc: "January 2027. 5–10 hrs/week. A live product, a portfolio piece, and a letter of rec.",
        href: "/admissions/2027-startup-sprint",
        primary: false,
    },
    {
        label: "28 days",
        title: "Founder Track",
        desc: "February 2027. 8–12 hrs/week. Full build cycle, deeper iteration, and a founder mentor.",
        href: "/admissions/founder-track",
        primary: false,
    },
    {
        label: "48 days",
        title: "The Residency",
        desc: "The most selective program we offer. A C-level title, a real company, and the deepest experience we have.",
        href: "/admissions/residency",
        primary: false,
    },
];

// ── Main component ────────────────────────────────────────────────────────────

export default function MainStudentNeedToKnowComponent() {
    return (
        <div className="body-font min-h-screen" style={{ background: "#faf8f5", color: "#2a2118" }}>
            <style>{fadeIn}</style>
            <NavigationBar transparent />

            {/* ── HERO ─────────────────────────────────────────────────────── */}
            <section className="relative pt-32 pb-16 md:pb-28 px-5 overflow-hidden" style={{ background: "#1e1710" }}>
                <div className="grain absolute inset-0 z-0" />
                <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-10"
                     style={{ background: "radial-gradient(circle, #b07d4a 0%, transparent 70%)" }} />
                <div className="relative z-10 max-w-4xl mx-auto text-center">
                    <div className="anim-1 inline-block mb-5 px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase"
                         style={{ background: "rgba(176,125,74,0.15)", color: "#c9956a", border: "1px solid rgba(176,125,74,0.3)", letterSpacing: "0.12em" }}>
                        For Students
                    </div>
                    <h1 className="anim-2 display-font text-4xl md:text-7xl font-black leading-tight md:leading-none mb-6 md:mb-8"
                        style={{ color: "#faf8f5", letterSpacing: "-0.02em" }}>
                        You have the idea.<br />
                        <span style={{ color: "#b07d4a", fontStyle: "italic" }}>Time to build the proof.</span>
                    </h1>
                    <p className="anim-3 body-font text-base md:text-xl leading-relaxed max-w-2xl mx-auto mb-8 md:mb-10"
                       style={{ color: "rgba(250,248,245,0.6)", fontWeight: 300 }}>
                        Extern Academy puts you on a real startup team for 14, 28, or 48 days. You own a role. You ship a product that goes live. You walk away with something you can point to in <i className="font-bold">every room</i> that matters.
                    </p>
                    <div className="anim-4 flex flex-col sm:flex-row gap-3 justify-center">
                        <a href="/apply"
                           className="px-7 py-3.5 rounded-full font-semibold text-sm md:text-base transition-all duration-200 text-decoration-none"
                           style={{ background: "#b07d4a", color: "#fff" }}
                           onMouseEnter={e => e.currentTarget.style.background = "#96683b"}
                           onMouseLeave={e => e.currentTarget.style.background = "#b07d4a"}
                        >
                            Apply Free. Takes 30 min →
                        </a>
                        <a href="#how-it-works"
                           className="px-7 py-3.5 rounded-full font-semibold text-sm md:text-base transition-all duration-200 text-decoration-none"
                           style={{ background: "rgba(250,248,245,0.08)", color: "rgba(250,248,245,0.8)", border: "1px solid rgba(250,248,245,0.15)" }}
                        >
                            See How It Works
                        </a>
                    </div>
                </div>
            </section>

            {/* ── WHAT THIS IS ─────────────────────────────────────────────── */}
            <section className="py-16 md:py-28 px-5">
                <div className="max-w-5xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-start">
                        <div>
                            <p className="text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: "#b07d4a", letterSpacing: "0.12em" }}>What This Is</p>
                            <h2 className="display-font text-3xl md:text-5xl font-black mb-5 leading-tight" style={{ letterSpacing: "-0.02em" }}>
                                <span style={{ fontStyle: "italic" }}>A startup.</span>
                            </h2>
                            <p className="body-font text-base leading-relaxed mb-4" style={{ color: "#6b5d4f", fontWeight: 300 }}>
                                You join a team of high school students. Each member owns a real role — Product, Engineering, Design, Marketing, Growth, or Operations. You have a Startup Trainer: a founder who has raised capital and shipped to real users. And you have a deadline.
                            </p>
                            <p className="body-font text-base leading-relaxed" style={{ color: "#6b5d4f", fontWeight: 300 }}>
                                By the end, you've built and launched a real consumer product. Something professional-grade you can showcase whenever opportunity calls for it.
                            </p>
                        </div>
                        <div className="space-y-3">
                            {[
                                ["No prerequisites", "No coding background, no business experience, no connections. Just drive."],
                                ["A real role", "You own one domain for the full program. You go deep, not wide."],
                                ["A real deadline", "You're not doing a simulation. You're shipping something that either launches or doesn't."],
                                ["A real mentor", "Your Startup Trainer has raised $1M+ and scaled to 100k+ users. A builder with venture-backed startup experience."],
                            ].map(([title, desc]) => (
                                <div key={title} className="flex gap-4 p-4 rounded-2xl" style={{ background: "#fff", border: "1px solid rgba(168,150,128,0.18)" }}>
                                    <span style={{ color: "#b07d4a", fontSize: "1rem", marginTop: "2px", flexShrink: 0 }}>✓</span>
                                    <div>
                                        <p className="font-semibold text-sm mb-1" style={{ color: "#2a2118" }}>{title}</p>
                                        <p className="text-sm" style={{ color: "#8a7565", fontWeight: 300 }}>{desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <div className="max-w-5xl mx-auto px-5">
                <div style={{ height: "1px", background: "linear-gradient(90deg, transparent, rgba(176,125,74,0.3), transparent)" }} />
            </div>

            {/* ── YOUR ROLE ────────────────────────────────────────────────── */}
            <section className="py-16 md:py-28 px-5" style={{ background: "#fdf9f5" }}>
                <div className="max-w-5xl mx-auto">
                    <p className="text-xs font-semibold tracking-widest uppercase mb-3 text-center" style={{ color: "#b07d4a", letterSpacing: "0.12em" }}>The Six Roles</p>
                    <h2 className="display-font text-3xl md:text-5xl font-black mb-3 text-center leading-tight" style={{ letterSpacing: "-0.02em" }}>
                        You own one domain.<br /><span style={{ fontStyle: "italic" }}>All the way through.</span>
                    </h2>
                    <p className="body-font text-center text-sm md:text-base mb-10 md:mb-16 max-w-2xl mx-auto" style={{ color: "#6b5d4f", fontWeight: 300 }}>
                        Every role is essential. Every role ships something real. Pick the one that fits how you think.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[
                            {
                                role: "Product Lead",
                                tag: "Strategy",
                                desc: "Follow the roadmap, lead the team to deployment day. This is the CEO role.",
                                fit: "Great if you like leading, making decisions, and connecting ideas to outcomes.",
                            },
                            {
                                role: "Engineering",
                                tag: "Technical",
                                desc: "Build the product. Pick up modern tools and AI-assisted development. The best part of writing code is pushing it live.",
                                fit: "Great if you like building, problem-solving, and seeing something work.",
                            },
                            {
                                role: "Design",
                                tag: "Creative",
                                desc: "Create the experience. You decide how the product looks, feels, and flows. You make it something people want to trust.",
                                fit: "Great if you like visual thinking, user empathy, and making things feel right.",
                            },
                            {
                                role: "Marketing",
                                tag: "Acquisition",
                                desc: "Find the first users and tell the story. You run outreach, write copy, and measure what works.",
                                fit: "Great if you like writing, storytelling, and figuring out what makes something spread.",
                            },
                            {
                                role: "Growth",
                                tag: "Hockey Stick",
                                desc: "Achieve hockey stick growth. You run experiments, optimize the listing, seed early communities, and figure out what works.",
                                fit: "Great if you're data-driven, scrappy, and obsessed with why something spreads.",
                            },
                            {
                                role: "Operations",
                                tag: "Systems",
                                desc: "Keep the team running. You remove blockers and make sure nothing falls through the cracks.",
                                fit: "Great if you're organized, process-driven, and the person who actually follows through.",
                            },
                        ].map(({ role, tag, desc, fit }) => (
                            <div key={role} className="p-6 rounded-3xl flex flex-col" style={{ background: "#fff", border: "1px solid rgba(168,150,128,0.2)" }}>
                                <span className="inline-block text-xs font-semibold tracking-widest uppercase mb-3 px-3 py-1 rounded-full self-start"
                                      style={{ background: "rgba(176,125,74,0.1)", color: "#b07d4a", letterSpacing: "0.1em" }}>
                                    {tag}
                                </span>
                                <h3 className="display-font text-xl font-bold mb-2" style={{ color: "#2a2118" }}>{role}</h3>
                                <p className="text-sm leading-relaxed mb-3 flex-1" style={{ color: "#6b5d4f", fontWeight: 300 }}>{desc}</p>
                                <p className="text-xs leading-relaxed italic" style={{ color: "#b07d4a", fontWeight: 400 }}>{fit}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── HOW IT WORKS ─────────────────────────────────────────────── */}
            <section id="how-it-works" className="py-16 md:py-28 px-5">
                <div className="max-w-5xl mx-auto">
                    <p className="text-xs font-semibold tracking-widest uppercase mb-3 text-center" style={{ color: "#b07d4a", letterSpacing: "0.12em" }}>The Program</p>
                    <h2 className="display-font text-3xl md:text-5xl font-black mb-3 text-center leading-tight" style={{ letterSpacing: "-0.02em" }}>
                        From zero to live.<br /><span style={{ fontStyle: "italic" }}>In 14 days.</span>
                    </h2>
                    <p className="body-font text-center text-sm md:text-base mb-10 md:mb-16 max-w-2xl mx-auto" style={{ color: "#6b5d4f", fontWeight: 300 }}>
                        Every program follows the same arc: idea → build → ship → present. The Founder Track and Residency give you more time to iterate. The Sprint is pure velocity.
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-12 md:mb-20">
                        {[
                            { step: "Day 1–2", title: "Align", desc: "Your team picks the product, maps the market, and locks in everyone's role." },
                            { step: "Day 3–9", title: "Build", desc: "You execute in your role. Standups every other day. Real feedback, real decisions." },
                            { step: "Day 10–13", title: "Ship", desc: "The product goes live. Real users can download or access it. No more hypotheticals." },
                            { step: "Day 14", title: "Demo Day", desc: "You present to real founders. Live demo. No slide decks. The finish line." },
                        ].map(({ step, title, desc }) => (
                            <div key={step} className="relative p-5 md:p-7 rounded-3xl overflow-hidden" style={{ background: "#1e1710" }}>
                                <div className="grain absolute inset-0 z-0" />
                                <p className="relative z-10 text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: "#b07d4a", letterSpacing: "0.12em" }}>{step}</p>
                                <h3 className="relative z-10 display-font text-lg md:text-xl font-bold mb-2" style={{ color: "#faf8f5" }}>{title}</h3>
                                <p className="relative z-10 text-xs md:text-sm leading-relaxed" style={{ color: "rgba(250,248,245,0.55)", fontWeight: 300 }}>{desc}</p>
                            </div>
                        ))}
                    </div>

                    {/* Trainer callout */}
                    <div className="rounded-3xl p-7 md:p-14 flex flex-col md:flex-row gap-6 md:gap-10 items-start"
                         style={{ background: "#fff", border: "1px solid rgba(168,150,128,0.2)" }}>
                        <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center text-3xl md:text-4xl"
                             style={{ background: "rgba(176,125,74,0.1)" }}>
                            🏆
                        </div>
                        <div>
                            <p className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: "#b07d4a", letterSpacing: "0.12em" }}>Your Startup Trainer</p>
                            <h3 className="display-font text-2xl md:text-3xl font-bold mb-3 leading-tight" style={{ letterSpacing: "-0.01em" }}>
                                Not a professor. A founder who has won.
                            </h3>
                            <p className="body-font text-sm md:text-base leading-relaxed" style={{ color: "#6b5d4f", fontWeight: 300 }}>
                                Every Extern Academy trainer has raised over <strong style={{ color: "#2a2118" }}>$1 million in funding</strong> and scaled a real product to <strong style={{ color: "#2a2118" }}>100,000+ users</strong>. They run your standups, stay available on Slack throughout the program, and write your letter of recommendation at the end. You're getting direct access to someone who has actually done what you're trying to do.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── WHAT YOU WALK AWAY WITH ──────────────────────────────────── */}
            <section className="py-16 md:py-28 px-5" style={{ background: "#1e1710" }}>
                <div className="relative max-w-5xl mx-auto">
                    <p className="text-xs font-semibold tracking-widest uppercase mb-3 text-center" style={{ color: "#b07d4a", letterSpacing: "0.12em" }}>What You Walk Away With</p>
                    <h2 className="display-font text-3xl md:text-5xl font-black mb-3 text-center leading-tight"
                        style={{ color: "#faf8f5", letterSpacing: "-0.02em" }}>
                        The answer to<br /><span style={{ fontStyle: "italic", color: "#b07d4a" }}>"Tell me about yourself."</span>
                    </h2>
                    <p className="body-font text-center text-sm md:text-base mb-10 md:mb-16 max-w-xl mx-auto" style={{ color: "rgba(250,248,245,0.5)", fontWeight: 300 }}>
                        Most students have the same answer. You won't.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[
                            { icon: "📦", title: "A live product", desc: "A deployed app that stays online for a complete year. You can link to it, demo it, and show it to anyone who asks." },
                            { icon: "✉️", title: "Letter of recommendation", desc: "A letter of recommendation from your startup trainer based on real work they watched you do. For college apps and internships." },
                            { icon: "📜", title: "Certificate of completion", desc: "Official recognition from the most hands-on startup program built for high school students." },
                            { icon: "🌐", title: "Your first real network", desc: "The teammates you build with are the founders, engineers, and operators you'll know for years. This is how professional networks actually start." },
                            { icon: "🧠", title: "The builder mindset", desc: "You stop asking 'can I?' and start asking 'how fast can we?' That shift is permanent. It changes everything that comes after." },
                            { icon: "🎯", title: "Proof you can execute", desc: "Most people talk about building things. After this, you'll have shipped one. That's a different conversation entirely." },
                        ].map(({ icon, title, desc }) => (
                            <div key={title} className="p-6 md:p-8 rounded-3xl" style={{ background: "rgba(250,248,245,0.04)", border: "1px solid rgba(250,248,245,0.08)" }}>
                                <div className="text-2xl md:text-3xl mb-3">{icon}</div>
                                <h3 className="display-font text-lg md:text-xl font-bold mb-2" style={{ color: "#faf8f5" }}>{title}</h3>
                                <p className="text-sm leading-relaxed" style={{ color: "rgba(250,248,245,0.5)", fontWeight: 300 }}>{desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── PROGRAMS ─────────────────────────────────────────────────── */}
            <section className="py-16 md:py-28 px-5">
                <div className="max-w-4xl mx-auto">
                    <p className="text-xs font-semibold tracking-widest uppercase mb-3 text-center" style={{ color: "#b07d4a", letterSpacing: "0.12em" }}>The Programs</p>
                    <h2 className="display-font text-3xl md:text-5xl font-black mb-3 text-center leading-tight" style={{ letterSpacing: "-0.02em" }}>
                        3 programs.<br /><span style={{ fontStyle: "italic" }}>1 direction.</span>
                    </h2>
                    <p className="body-font text-center text-sm md:text-base mb-10 md:mb-14 max-w-xl mx-auto" style={{ color: "#6b5d4f", fontWeight: 300 }}>
                        All three end with a live product, a certificate, and a letter of rec. The question is how deep you want to go.
                    </p>
                    <div className="space-y-3 mb-10">
                        {[
                            {
                                name: "2027 Startup Sprint",
                                duration: "14 days · Jan 2027",
                                hours: "5–10 hrs/week",
                                desc: "Pure velocity. You join a team of 5, take a role, and launch a free consumer product in two weeks. No prior experience needed. Just show up ready to build.",
                                href: "/admissions/2027-startup-sprint",
                                tag: null,
                            },
                            {
                                name: "Founder Track",
                                duration: "28 days · Feb 2027",
                                hours: "8–12 hrs/week",
                                desc: "More iteration, more depth. You have four weeks to build, launch, gather real user feedback, and improve. The product you finish is more polished and the story you can tell is stronger.",
                                href: "/admissions/founder-track",
                                tag: "Most popular",
                            },
                            {
                                name: "The Residency",
                                duration: "48 days · May 2027",
                                hours: "3–4 hrs/day",
                                desc: "The most selective program we offer. You hold a C-level title and help build a real company from the ground up. For students who want to go all in.",
                                href: "/admissions/residency",
                                tag: "Most selective",
                            },
                        ].map(({ name, duration, hours, desc, href, tag }) => (
                            <div key={name} className="p-5 md:p-7 rounded-2xl flex flex-col md:flex-row md:items-center gap-4 md:gap-6"
                                 style={{ background: "#fff", border: "1px solid rgba(168,150,128,0.2)" }}>
                                <div className="flex-1">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <h3 className="display-font text-lg md:text-xl font-bold" style={{ color: "#2a2118" }}>{name}</h3>
                                        {tag && (
                                            <span className="text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full"
                                                  style={{ background: "rgba(176,125,74,0.1)", color: "#b07d4a", letterSpacing: "0.1em" }}>
                                                {tag}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs mb-2" style={{ color: "#b07d4a", fontWeight: 500 }}>{duration} · {hours}</p>
                                    <p className="text-sm leading-relaxed" style={{ color: "#6b5d4f", fontWeight: 300 }}>{desc}</p>
                                </div>
                                <a href={href}
                                   className="flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-semibold text-decoration-none transition-all duration-200 text-center"
                                   style={{ background: "#b07d4a", color: "#fff", whiteSpace: "nowrap" }}
                                   onMouseEnter={e => e.currentTarget.style.background = "#96683b"}
                                   onMouseLeave={e => e.currentTarget.style.background = "#b07d4a"}
                                >
                                    Learn More →
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── FAQ ──────────────────────────────────────────────────────── */}
            <section className="py-16 md:py-28 px-5" style={{ background: "#fdf9f5" }}>
                <div className="max-w-3xl mx-auto">
                    <p className="text-xs font-semibold tracking-widest uppercase mb-3 text-center" style={{ color: "#b07d4a", letterSpacing: "0.12em" }}>Common Questions</p>
                    <h2 className="display-font text-3xl md:text-5xl font-black mb-10 md:mb-14 text-center leading-tight" style={{ letterSpacing: "-0.02em" }}>
                        Things students always ask.
                    </h2>
                    <div>
                        {faqs.map(faq => <FAQ key={faq.q} {...faq} />)}
                    </div>
                </div>
            </section>

            {/* ── FINAL CTA ────────────────────────────────────────────────── */}
            <section className="py-16 md:py-20 px-4" style={{ background: "#0f0c09" }}>
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-8 md:mb-12">
                        <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ color: "#b07d4a", letterSpacing: "0.14em" }}>
                            Next Steps
                        </p>
                        <h2 className="display-font text-2xl md:text-4xl font-bold mb-3" style={{ color: "#faf8f5" }}>
                            Ready to build something real?
                        </h2>
                        <p className="text-sm md:text-base max-w-xl mx-auto" style={{ color: "rgba(250,248,245,0.45)" }}>
                            The application is free and takes 30 minutes. Cohorts are small by design. Spots close when they fill.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {ctaCards.map(({ label, title, desc, href, primary }) => (
                            <a
                                key={href}
                                href={href}
                                className="group flex flex-col rounded-2xl p-5 md:p-6 text-decoration-none transition-all duration-200"
                                style={{
                                    background: primary ? "#b07d4a" : "rgba(250,248,245,0.05)",
                                    border: primary ? "none" : "1px solid rgba(250,248,245,0.1)",
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = primary ? "#96683b" : "rgba(250,248,245,0.09)"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = primary ? "#b07d4a" : "rgba(250,248,245,0.05)"; }}
                            >
                                <span className="text-xs font-semibold tracking-widest uppercase mb-3"
                                      style={{ color: primary ? "rgba(255,255,255,0.7)" : "#b07d4a", letterSpacing: "0.12em" }}>
                                    {label}
                                </span>
                                <h3 className="text-base md:text-lg font-bold mb-2" style={{ color: primary ? "#fff" : "#faf8f5" }}>
                                    {title}
                                </h3>
                                <p className="text-sm leading-relaxed flex-1" style={{ color: primary ? "rgba(255,255,255,0.75)" : "rgba(250,248,245,0.45)" }}>
                                    {desc}
                                </p>
                                <span className="mt-4 text-sm font-semibold" style={{ color: primary ? "#fff" : "#b07d4a" }}>
                                    {primary ? "Apply Now →" : "Learn More →"}
                                </span>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            <FooterComponent />
        </div>
    );
}
